import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';

const SCRIPT_ID = 'seopush-chatbot-widget';
const STYLE_GUARD_ID = 'seopush-chatbot-route-guard';
const SCRIPT_URL = 'https://api.seopush.ai/chatbot-widget.js?v=3';
const BOT_KEY = '714071bed8686217106e2d0339026254';
const API_BASE = 'https://api.seopush.ai';

/** Exact public marketing / legal paths that should show the chatbot */
const EXACT_PUBLIC_PATHS = new Set([
    '/',
    '/features',
    '/pricing',
    '/whats-new',
    '/about',
    '/blog',
    '/careers',
    '/help',
    '/api-docs',
    '/status',
    '/compare',
    '/login',
    '/quote',
    '/terms',
    '/privacy',
    '/accessibility',
]);

/** Nested public routes (articles, proposals, compare slugs) */
const PUBLIC_PREFIXES = ['/blog/', '/help/', '/compare/', '/proposal/'];

/**
 * Paid-traffic / transactional pages stay chatbot-free
 * (same spirit as PWAInstallBanner popup-free routes).
 */
const BLOCKED_PREFIXES = ['/start', '/lp/', '/payment/'];

function normalizePath(pathname) {
    if (!pathname) return '/';
    const p = String(pathname).toLowerCase();
    if (p.length > 1 && p.endsWith('/')) return p.slice(0, -1);
    return p || '/';
}

export function isSeoPushPublicRoute(pathname) {
    const p = normalizePath(pathname);

    if (BLOCKED_PREFIXES.some((prefix) => p === prefix.replace(/\/$/, '') || p.startsWith(prefix))) {
        return false;
    }

    if (EXACT_PUBLIC_PATHS.has(p)) return true;
    return PUBLIC_PREFIXES.some((prefix) => p.startsWith(prefix));
}

function ensureRouteGuardStyle() {
    if (document.getElementById(STYLE_GUARD_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_GUARD_ID;
    // [class*="sp-chat"] also covers overlay/backdrop nodes the widget may
    // create on mobile — leaving those behind blocks the whole page.
    style.textContent = [
        'body[data-seopush="off"] [class*="sp-chat"]{',
        'display:none!important;visibility:hidden!important;pointer-events:none!important;',
        '}',
    ].join('');
    document.head.appendChild(style);
}

function removeSeoPushDom() {
    document.querySelectorAll('[class*="sp-chat"]').forEach((el) => el.remove());
    document.querySelectorAll('style').forEach((style) => {
        if (style.id === STYLE_GUARD_ID) return;
        if (style.textContent && style.textContent.includes('.sp-chat-fab')) {
            style.remove();
        }
    });
    document.documentElement.style.removeProperty('--sp-color');
}

function destroySeoPushWidget() {
    document.getElementById(SCRIPT_ID)?.remove();
    removeSeoPushDom();
}

/** Strip FABs that appear after async widget init once we've already left a public route. */
function watchForStaleWidgetNodes() {
    const observer = new MutationObserver(() => {
        if (document.body.dataset.seopush !== 'off') return;
        if (document.querySelector('[class*="sp-chat"]')) {
            removeSeoPushDom();
        }
    });
    observer.observe(document.body, { childList: true });
    // Stop watching shortly after — config fetch usually finishes within a few seconds.
    window.setTimeout(() => observer.disconnect(), 15000);
    return observer;
}

/**
 * v3 widget resolves the host tag via currentScript OR querySelector on
 * script[src*="chatbot-widget.js"][data-bot-key], and reads data-api-base
 * directly — so a normal async <script src> injection is enough.
 */
function injectSeoPushWidget() {
    if (document.getElementById(SCRIPT_ID)) return;
    if (document.querySelector('.sp-chat-fab')) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.setAttribute('data-bot-key', BOT_KEY);
    script.setAttribute('data-api-base', API_BASE);
    script.onerror = () => {
        console.error('[SeoPush] Failed to load chatbot script');
        script.remove();
    };
    document.body.appendChild(script);
}

/**
 * Loads the SeoPush Q&A chatbot only on public marketing / help pages.
 * Removes the floating FAB + chat window when entering the authenticated app.
 */
export default function SeoPushChatbot() {
    const { pathname } = useLocation();
    const { branding, loading: brandingLoading } = useBranding();

    useEffect(() => {
        ensureRouteGuardStyle();

        if (brandingLoading) return undefined;

        // White-label domains are the product login shell, not Email Lion marketing.
        const show = !branding?.whitelabel && isSeoPushPublicRoute(pathname);
        document.body.dataset.seopush = show ? 'on' : 'off';

        if (!show) {
            destroySeoPushWidget();
            const observer = watchForStaleWidgetNodes();
            return () => observer.disconnect();
        }

        injectSeoPushWidget();
        return undefined;
    }, [pathname, branding?.whitelabel, brandingLoading]);

    useEffect(() => {
        return () => {
            document.body.dataset.seopush = 'off';
            destroySeoPushWidget();
        };
    }, []);

    return null;
}
